import React from 'react';
import {
  ChatBubbleLeftRightIcon,
  PaperAirplaneIcon,
  UserGroupIcon,
  CheckCircleIcon,
  PaperClipIcon,
  MagnifyingGlassIcon,
  BellIcon,
  UserIcon,
  HashtagIcon,
  PhotoIcon,
  DocumentIcon,
  LinkIcon,
  FaceSmileIcon,
  ClockIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';

export default function ProjectChatHelp() {
  return (
    <div className="min-h-screen bg-cover bg-center bg-gradient-to-b from-indigo-300 via-sky-100 to-white w-full">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
              <ChatBubbleLeftRightIcon className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Project Communication</h1>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Real-time project communication platform with threaded conversations, file sharing,
            and team collaboration tools for efficient project coordination.
          </p>
        </div>

        {/* System Overview */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-6">
            <div className="flex items-center">
              <ChatBubbleLeftRightIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Project Chat System Overview</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-200">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mb-4">
                  <ChatBubbleLeftRightIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Real-Time Messaging</h3>
                <p className="text-blue-700 text-sm leading-relaxed">
                  Instant messaging with read receipts, typing indicators, and message
                  threading for organized project discussions and quick communication.
                </p>
              </div>

              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 border border-indigo-200">
                <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center mb-4">
                  <PaperClipIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">File Sharing</h3>
                <p className="text-indigo-700 text-sm leading-relaxed">
                  Secure file sharing with version control, preview capabilities,
                  and integration with project documents and deliverables.
                </p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-200">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-600 rounded-xl flex items-center justify-center mb-4">
                  <HashtagIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Channel Organization</h3>
                <p className="text-purple-700 text-sm leading-relaxed">
                  Organized communication channels for different project aspects,
                  teams, and topics with customizable permissions and access control.
                </p>
              </div>

              <div className="bg-gradient-to-br from-pink-50 to-red-50 rounded-2xl p-6 border border-pink-200">
                <div className="w-12 h-12 bg-gradient-to-r from-pink-500 to-red-600 rounded-xl flex items-center justify-center mb-4">
                  <BellIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Notifications</h3>
                <p className="text-pink-700 text-sm leading-relaxed">
                  Smart notification system with customizable alerts, mention tracking,
                  and priority-based notification management for important updates.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Interface Button Layouts */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-8 py-6">
            <div className="flex items-center">
              <CheckCircleIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Interface Button Layouts</h2>
            </div>
          </div>

          <div className="p-8">
            {/* Chat Interface Layout */}
            <div className="bg-white rounded-xl p-6 mb-8 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Main Chat Interface</h3>

              <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-6 border border-gray-200">
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                  {/* Channels Sidebar */}
                  <div className="lg:col-span-1">
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                      <h4 className="font-semibold text-gray-900 mb-3">Channels</h4>
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2 p-2 rounded-lg bg-blue-50 border border-blue-200">
                          <HashtagIcon className="h-4 w-4 text-blue-600" />
                          <span className="text-sm font-medium text-blue-900">general</span>
                        </div>
                        <div className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-50">
                          <HashtagIcon className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-700">development</span>
                        </div>
                        <div className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-50">
                          <HashtagIcon className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-700">design</span>
                        </div>
                        <div className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-50">
                          <HashtagIcon className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-700">testing</span>
                        </div>
                      </div>

                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <h4 className="font-semibold text-gray-900 mb-3">Direct Messages</h4>
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-50">
                            <UserIcon className="h-4 w-4 text-gray-400" />
                            <span className="text-sm text-gray-700">John Smith</span>
                          </div>
                          <div className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-50">
                            <UserIcon className="h-4 w-4 text-gray-400" />
                            <span className="text-sm text-gray-700">Sarah Johnson</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Chat Area */}
                  <div className="lg:col-span-3">
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                      {/* Chat Header */}
                      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <HashtagIcon className="h-5 w-5 text-white" />
                            <h3 className="text-lg font-semibold text-white">general</h3>
                            <span className="text-blue-100 text-sm">8 members</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button className="text-white hover:text-blue-100 opacity-75 cursor-not-allowed">
                              <MagnifyingGlassIcon className="h-5 w-5" />
                            </button>
                            <button className="text-white hover:text-blue-100 opacity-75 cursor-not-allowed">
                              <UserGroupIcon className="h-5 w-5" />
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Messages Area */}
                      <div className="h-96 overflow-y-auto p-4 space-y-4">
                        {/* Message 1 */}
                        <div className="flex space-x-3">
                          <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-white text-sm font-medium">JS</span>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <span className="font-medium text-gray-900">John Smith</span>
                              <span className="text-xs text-gray-500">10:30 AM</span>
                            </div>
                            <p className="text-gray-700">Good morning team! Let's review the sprint progress today.</p>
                          </div>
                        </div>

                        {/* Message 2 */}
                        <div className="flex space-x-3">
                          <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-white text-sm font-medium">SJ</span>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <span className="font-medium text-gray-900">Sarah Johnson</span>
                              <span className="text-xs text-gray-500">10:32 AM</span>
                            </div>
                            <p className="text-gray-700">I've completed the user authentication module. Ready for code review.</p>
                            <div className="mt-2 flex items-center space-x-2">
                              <PaperClipIcon className="h-4 w-4 text-gray-400" />
                              <span className="text-sm text-blue-600 hover:text-blue-800 cursor-pointer opacity-75">auth-module-v1.2.zip</span>
                            </div>
                          </div>
                        </div>

                        {/* Message 3 */}
                        <div className="flex space-x-3">
                          <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-white text-sm font-medium">MD</span>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <span className="font-medium text-gray-900">Mike Davis</span>
                              <span className="text-xs text-gray-500">10:35 AM</span>
                            </div>
                            <p className="text-gray-700">
                              @Sarah Great work! I'll review it this afternoon. Also, I found a potential issue with the API endpoints.
                              Let me share the error logs.
                            </p>
                            <div className="mt-2 bg-gray-50 rounded-lg p-3 border border-gray-200">
                              <div className="flex items-center space-x-2 mb-2">
                                <DocumentIcon className="h-4 w-4 text-gray-400" />
                                <span className="text-sm font-medium text-gray-900">api-errors.log</span>
                              </div>
                              <pre className="text-xs text-gray-600 bg-white p-2 rounded border overflow-x-auto">
{`ERROR: 500 Internal Server Error
Endpoint: /api/users/login
Time: 2025-10-14 10:30:15`}
                              </pre>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Message Input */}
                      <div className="border-t border-gray-200 p-4">
                        <div className="flex items-end space-x-3">
                          <div className="flex-1">
                            <div className="relative">
                              <textarea
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-10 resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent opacity-75 cursor-not-allowed"
                                rows="2"
                                placeholder="Type your message..."
                                disabled
                              />
                              <button className="absolute right-2 bottom-2 text-gray-400 hover:text-gray-600 opacity-75 cursor-not-allowed">
                                <FaceSmileIcon className="h-5 w-5" />
                              </button>
                            </div>
                          </div>

                          <div className="flex items-center space-x-2">
                            <button className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 opacity-75 cursor-not-allowed">
                              <PaperClipIcon className="h-5 w-5" />
                            </button>
                            <button className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 opacity-75 cursor-not-allowed">
                              <PhotoIcon className="h-5 w-5" />
                            </button>
                            <button className="bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700 transition-colors opacity-75 cursor-not-allowed">
                              <PaperAirplaneIcon className="h-5 w-5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Message Actions */}
            <div className="bg-white rounded-xl p-6 mb-8 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Message Actions & Features</h3>

              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <button className="flex flex-col items-center p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors opacity-75 cursor-not-allowed">
                    <ChatBubbleLeftRightIcon className="h-8 w-8 text-blue-600 mb-2" />
                    <span className="text-sm font-medium text-gray-900">Reply</span>
                    <span className="text-xs text-gray-500">Thread conversation</span>
                  </button>

                  <button className="flex flex-col items-center p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors opacity-75 cursor-not-allowed">
                    <BellIcon className="h-8 w-8 text-yellow-600 mb-2" />
                    <span className="text-sm font-medium text-gray-900">Mention</span>
                    <span className="text-xs text-gray-500">@username</span>
                  </button>

                  <button className="flex flex-col items-center p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors opacity-75 cursor-not-allowed">
                    <EyeIcon className="h-8 w-8 text-green-600 mb-2" />
                    <span className="text-sm font-medium text-gray-900">Read Status</span>
                    <span className="text-xs text-gray-500">Message seen</span>
                  </button>

                  <button className="flex flex-col items-center p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors opacity-75 cursor-not-allowed">
                    <ClockIcon className="h-8 w-8 text-purple-600 mb-2" />
                    <span className="text-sm font-medium text-gray-900">Timestamp</span>
                    <span className="text-xs text-gray-500">Message time</span>
                  </button>
                </div>
              </div>
            </div>

            {/* File Sharing Interface */}
            <div className="bg-white rounded-xl p-6 mb-8 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4">File Sharing & Media</h3>

              <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
                <div className="bg-gradient-to-r from-green-600 to-blue-600 px-6 py-4">
                  <h2 className="text-xl font-bold text-white">Shared Files</h2>
                </div>

                <div className="p-6">
                  <div className="space-y-4">
                    {/* File Item 1 */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                          <DocumentIcon className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">project-requirements.pdf</p>
                          <p className="text-sm text-gray-600">Uploaded by John Smith • 2.3 MB • Oct 14, 2025</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button className="text-blue-600 hover:text-blue-800 opacity-75 cursor-not-allowed">
                          <EyeIcon className="h-5 w-5" />
                        </button>
                        <button className="text-green-600 hover:text-green-800 opacity-75 cursor-not-allowed">
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {/* File Item 2 */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                          <PhotoIcon className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">wireframe-design.png</p>
                          <p className="text-sm text-gray-600">Uploaded by Sarah Johnson • 1.8 MB • Oct 13, 2025</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button className="text-blue-600 hover:text-blue-800 opacity-75 cursor-not-allowed">
                          <EyeIcon className="h-5 w-5" />
                        </button>
                        <button className="text-green-600 hover:text-green-800 opacity-75 cursor-not-allowed">
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {/* File Item 3 */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
                          <LinkIcon className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">API Documentation</p>
                          <p className="text-sm text-gray-600">Shared by Mike Davis • Link • Oct 12, 2025</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button className="text-blue-600 hover:text-blue-800 opacity-75 cursor-not-allowed">
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Feature Explanations */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
              <div className="bg-white rounded-xl p-6 border border-blue-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">1</span>
                  </div>
                  <h4 className="text-lg font-bold text-blue-900">Real-Time Communication</h4>
                </div>
                <p className="text-blue-700 text-sm leading-relaxed">
                  Instant messaging with typing indicators, read receipts, and message status
                  tracking to ensure effective team communication and coordination.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-indigo-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-indigo-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">2</span>
                  </div>
                  <h4 className="text-lg font-bold text-indigo-900">Organized Channels</h4>
                </div>
                <p className="text-indigo-700 text-sm leading-relaxed">
                  Structured communication channels for different project aspects, teams,
                  and topics with customizable permissions and access controls.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-purple-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">3</span>
                  </div>
                  <h4 className="text-lg font-bold text-purple-900">Rich File Sharing</h4>
                </div>
                <p className="text-purple-700 text-sm leading-relaxed">
                  Comprehensive file sharing with preview capabilities, version control,
                  and integration with project documents and deliverables.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-pink-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-pink-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">4</span>
                  </div>
                  <h4 className="text-lg font-bold text-pink-900">Smart Notifications</h4>
                </div>
                <p className="text-pink-700 text-sm leading-relaxed">
                  Intelligent notification system with mention tracking, priority-based alerts,
                  and customizable notification preferences for different channels.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-emerald-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">5</span>
                  </div>
                  <h4 className="text-lg font-bold text-emerald-900">Message Threading</h4>
                </div>
                <p className="text-emerald-700 text-sm leading-relaxed">
                  Threaded conversations for organized discussions, keeping related messages
                  together and maintaining conversation context and history.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-yellow-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-yellow-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">6</span>
                  </div>
                  <h4 className="text-lg font-bold text-yellow-900">Search & History</h4>
                </div>
                <p className="text-yellow-700 text-sm leading-relaxed">
                  Powerful search functionality across all messages and files with advanced
                  filters, date ranges, and full conversation history preservation.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Best Practices */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-8 py-6">
            <div className="flex items-center">
              <CheckCircleIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Project Communication Best Practices</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                  <h3 className="text-lg font-bold text-blue-900 mb-3">Channel Organization</h3>
                  <ul className="space-y-2 text-sm text-blue-700">
                    <li>• Create channels for specific project aspects</li>
                    <li>• Use clear naming conventions for channels</li>
                    <li>• Set appropriate permissions for each channel</li>
                    <li>• Archive inactive channels regularly</li>
                    <li>• Pin important messages and resources</li>
                  </ul>
                </div>

                <div className="bg-indigo-50 rounded-xl p-6 border border-indigo-200">
                  <h3 className="text-lg font-bold text-indigo-900 mb-3">Effective Communication</h3>
                  <ul className="space-y-2 text-sm text-indigo-700">
                    <li>• Use @mentions for important notifications</li>
                    <li>• Keep messages clear and concise</li>
                    <li>• Use threads for detailed discussions</li>
                    <li>• Share files with descriptive names</li>
                    <li>• Respond promptly to messages and mentions</li>
                  </ul>
                </div>
              </div>

              <div className="space-y-6">
                <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
                  <h3 className="text-lg font-bold text-purple-900 mb-3">File Management</h3>
                  <ul className="space-y-2 text-sm text-purple-700">
                    <li>• Organize files in logical folder structures</li>
                    <li>• Use consistent naming conventions</li>
                    <li>• Add descriptions to shared files</li>
                    <li>• Regularly clean up outdated files</li>
                    <li>• Use version control for important documents</li>
                  </ul>
                </div>

                <div className="bg-pink-50 rounded-xl p-6 border border-pink-200">
                  <h3 className="text-lg font-bold text-pink-900 mb-3">Notification Management</h3>
                  <ul className="space-y-2 text-sm text-pink-700">
                    <li>• Customize notification preferences</li>
                    <li>• Use notification keywords strategically</li>
                    <li>• Set quiet hours for focused work</li>
                    <li>• Mute non-essential channels</li>
                    <li>• Review and organize notification settings</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}